'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { PLATFORM_BRAND_NAME, TenantBrand } from '@/components/brand/tenant-brand';
import { useOptionalNavSession } from '@/components/nav-session-provider';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * True while the root LearnerAccessBoundary is still waiting for its FIRST
 * verdict for the current learner (#2830). Routine in-app navigation carries the
 * previous verdict and never sets this; only an initial load or deep link does.
 * Layouts that host protected learner content consume it around their content
 * slot so the shell stays mounted while the page itself is withheld.
 */
export const LearnerAccessPendingContext = createContext(false);

export function useLearnerAccessPending() {
  return useContext(LearnerAccessPendingContext);
}

const LOADING_LABEL = 'Loading your learning';

function PendingStatus() {
  return <p role="status" aria-live="polite" className="sr-only">{LOADING_LABEL}</p>;
}

/**
 * Content-slot gate for the learner shell. Withholds protected page content
 * behind an honest, generic skeleton until the first access verdict lands.
 * Deliberately not destination-shaped (#2512): a pending page must not look
 * like a populated one. No focus move and no animation under reduced motion.
 */
export function LearnerContentGate({ children }: { children: ReactNode }) {
  if (!useLearnerAccessPending()) return children;
  // AppShell already applies the content reading-line wrapper around this slot,
  // so the skeleton carries no padding of its own and lines up with real content.
  return (
    <div data-testid="learner-content-pending">
      <PendingStatus />
      <div aria-hidden="true" className="space-y-4">
        <Skeleton aria-hidden="true" className="h-8 w-1/3 motion-reduce:animate-none" />
        <Skeleton aria-hidden="true" className="h-4 w-2/3 motion-reduce:animate-none" />
        <Skeleton aria-hidden="true" className="h-4 w-1/2 motion-reduce:animate-none" />
      </div>
    </div>
  );
}

/**
 * Full-viewport pending state for surfaces with no learner shell to keep
 * mounted (practice session deep links). Branded, static, and unfocusable.
 */
export function LearnerAccessPlaceholder() {
  const brand = useOptionalNavSession()?.brand ?? null;
  return (
    <main id="main" className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <PendingStatus />
      <div aria-hidden="true" className="flex flex-col items-center gap-6">
        <TenantBrand
          size="lg"
          brandName={brand?.brandName ?? PLATFORM_BRAND_NAME}
          logoLightUrl={brand?.logoLightUrl ?? null}
          logoDarkUrl={brand?.logoDarkUrl ?? null}
          theme={brand?.appliedTheme ?? 'dark'}
        />
        <Skeleton aria-hidden="true" className="h-2 w-40 motion-reduce:animate-none" />
      </div>
    </main>
  );
}
